import { SliderCore } from "../slider/core.js";
import { positionText, unknownText } from "../../../i18n/text/time.js";
import { seekText } from "../../../i18n/text/slider.js";
import { defaults } from "@videojs/utils/object";
import { getTimeRangeEnd, hasTimeRange } from "@videojs/media";
import { toPercent } from "@videojs/utils/number";
import { formatTimeAsPhrase } from "@videojs/utils/time";
//#region src/core/ui/time-slider/core.ts
/**
* Time-domain slider: maps media time/buffer state to slider state.
*
* @internal
*/
var TimeSliderCore = class TimeSliderCore extends SliderCore {
	static defaultProps = {
		...SliderCore.defaultProps,
		label: "",
		changeThrottle: 100,
		pauseOnDrag: false
	};
	#props = { ...TimeSliderCore.defaultProps };
	#media = null;
	#formatLocale;
	#wasPlayingBeforeDrag = false;
	#text;
	constructor(props) {
		super();
		if (props) this.setProps(props);
	}
	setProps(props) {
		this.#props = defaults(props, TimeSliderCore.defaultProps);
		super.setProps({
			...props,
			min: 0
		});
	}
	setMedia(media) {
		this.#media = media;
	}
	/** @internal Platform adapters set the active i18n locale for `aria-valuetext` time formatting. */
	setFormatLocale(locale) {
		if (this.#formatLocale !== locale) this.#text = void 0;
		this.#formatLocale = locale;
	}
	getState() {
		const media = this.#media;
		const { currentTime, seeking, buffered } = media;
		const duration = getTimeRangeEnd(media);
		super.setProps({
			...this.#props,
			disabled: this.#props.disabled || !hasTimeRange(media),
			min: 0,
			max: duration
		});
		const base = super.getSliderState(currentTime);
		const bufferedEnd = buffered.length > 0 ? buffered[buffered.length - 1][1] : 0;
		const bufferPercent = toPercent(bufferedEnd, 0, duration);
		return {
			...base,
			currentTime,
			duration,
			seeking,
			bufferPercent
		};
	}
	getLabel(state) {
		return super.getLabel(state) || seekText;
	}
	#announceValue(state) {
		return state.dragging ? this.rawValueFromPercent(state.pointerPercent) : state.value;
	}
	#formatTimeAsPhrase(seconds) {
		return this.#formatLocale === void 0 ? formatTimeAsPhrase(seconds) : formatTimeAsPhrase(seconds, { locale: this.#formatLocale });
	}
	getValueText(state) {
		if (state.duration <= 0) return unknownText;
		return Number.isFinite(state.duration) ? positionText : this.getValueTextParams(state).current;
	}
	getValueTextParams(state) {
		const current = Math.floor(this.#announceValue(state));
		const duration = Math.floor(state.duration);
		if (this.#text && this.#text.current === current && this.#text.duration === duration) return this.#text.params;
		const params = Number.isFinite(duration) ? {
			current: this.#formatTimeAsPhrase(current),
			duration: this.#formatTimeAsPhrase(duration)
		} : { current: this.#formatTimeAsPhrase(current) };
		this.#text = {
			current,
			duration,
			params
		};
		return params;
	}
	/**
	* Pause playback when a drag begins if `pauseOnDrag` is enabled, remembering whether media was playing so `endDrag`
	* can resume it.
	*/
	startDrag(playback) {
		this.#wasPlayingBeforeDrag = false;
		if (this.#props.pauseOnDrag && playback && !playback.paused) {
			this.#wasPlayingBeforeDrag = true;
			playback.pause();
		}
	}
	/**
	* Resume playback if `startDrag` paused it. Resume depends only on the intent captured at drag start, so it survives
	* `pauseOnDrag` being toggled mid-drag. Safe to call on teardown — a no-op unless a drag paused playback.
	*/
	endDrag(playback) {
		if (this.#wasPlayingBeforeDrag) playback?.play().catch(() => {});
		this.#wasPlayingBeforeDrag = false;
	}
	getAttrs(state) {
		const base = super.getAttrs(state);
		const announceValue = this.#announceValue(state);
		return {
			...base,
			"aria-valuenow": announceValue,
			"aria-valuetext": this.getValueText(state)
		};
	}
};
//#endregion
export { TimeSliderCore };
